import { findIndex } from 'lodash';

import { ReferenceSource } from '../models';

const SOURCE_MODE_EVENT = 'wk:rhp-source-editing-mode';
const SOURCE_CONTEXT_EVENT = 'wk:rhp-source-editing-context';
const SOURCE_NAVIGATE_EVENT = 'wk:rhp-source-editing-navigate';
const SOURCE_OPEN_RETRY_DELAY_MS = 50;
const SOURCE_OPEN_MAX_RETRIES = 30;

interface SourceEditingContext {
  ownerId: string;
  fromRhp: boolean;
  timestamp: number;
  selectedCitationId: string;
  citationString: string;
  referenceIndex: number;
}

const getSourceEditingOwnerId = (ctx: any): string => {
  const editorInstance = ctx?.ckeditor?.editorInstance;
  if (!editorInstance) {
    return '';
  }

  const wkOwnerId = editorInstance.__wkSourceEditingOwnerId as
    | string
    | undefined;
  if (typeof wkOwnerId === 'string' && wkOwnerId.length) {
    return wkOwnerId;
  }

  const nextOwnerId = String(
    editorInstance.id ||
      `wk-source-owner-${Date.now()}-${Math.floor(Math.random() * 1000)}`
  );
  editorInstance.__wkSourceEditingOwnerId = nextOwnerId;
  return nextOwnerId;
};

const shouldHandleSourceEditingEvent = (
  ctx: any,
  event: CustomEvent
): boolean => {
  const eventOwnerId = event?.detail?.ownerId;
  if (typeof eventOwnerId !== 'string' || !eventOwnerId.length) {
    return true;
  }

  return eventOwnerId === getSourceEditingOwnerId(ctx);
};

const publishSourceEditingContextWithRetry = (ctx: any, attempt = 0): void => {
  const isPublished = handleSourceEditingOpenedFromRhp(ctx);

  if (isPublished) {
    return;
  }

  if (attempt >= SOURCE_OPEN_MAX_RETRIES) {
    ctx.sourceEditingContext = null;
    return;
  }

  setTimeout(
    () => publishSourceEditingContextWithRetry(ctx, attempt + 1),
    SOURCE_OPEN_RETRY_DELAY_MS
  );
};

export const bindSourceEditingModeListener = (ctx: any): void => {
  if (ctx.sourceEditingModeListenerCleanup) {
    return;
  }

  ctx.sourceEditingModeListenerCleanup = ctx.renderer.listen(
    'window',
    SOURCE_MODE_EVENT,
    (event: CustomEvent) => {
      if (!shouldHandleSourceEditingEvent(ctx, event)) {
        return;
      }

      const mode = event?.detail?.mode;
      ctx.sourceEditingMode = mode === 'byReference' ? 'byReference' : 'full';
    }
  );
};

export const bindSourceEditingNavigateListener = (ctx: any): void => {
  if (ctx.sourceEditingNavigateListenerCleanup) {
    return;
  }

  ctx.sourceEditingNavigateListenerCleanup = ctx.renderer.listen(
    'window',
    SOURCE_NAVIGATE_EVENT,
    (event: CustomEvent) => {
      if (!shouldHandleSourceEditingEvent(ctx, event)) {
        return;
      }

      const direction = event?.detail?.direction;

      if (direction === 'next' || direction === 'previous') {
        navigateSourceEditingReference(ctx, direction);
      }
    }
  );
};

export const bindSourceEditingOpenListener = (ctx: any): void => {
  if (ctx.sourceEditingOpenListenerBound) {
    return;
  }

  const editSourceCommand =
    ctx.ckeditor?.editorInstance?.commands?.get('editSource');
  if (!editSourceCommand?.on) {
    return;
  }

  editSourceCommand.on('execute', () => {
    const ownerId = getSourceEditingOwnerId(ctx);

    ctx.sourceEditingMode = 'full';
    ctx.elementRef?.nativeElement?.ownerDocument?.defaultView?.dispatchEvent?.(
      new CustomEvent(SOURCE_MODE_EVENT, {
        detail: { mode: 'full', ownerId, fromRhp: true },
      })
    );

    publishSourceEditingContextWithRetry(ctx);
  });

  ctx.sourceEditingOpenListenerBound = true;
};

export const bindSourceEditingReplaceListener = (ctx: any): void => {
  if (ctx.sourceEditingReplaceListenerBound) {
    return;
  }

  const replaceSourceCommand =
    ctx.ckeditor?.editorInstance?.commands?.get('replaceSource');
  if (
    !replaceSourceCommand ||
    typeof replaceSourceCommand.execute !== 'function'
  ) {
    return;
  }

  const originalExecute =
    replaceSourceCommand.execute.bind(replaceSourceCommand);

  replaceSourceCommand.execute = (...args: any[]) => {
    const sourceContext = ctx.sourceEditingContext as SourceEditingContext;

    if (ctx.sourceEditingMode !== 'byReference') {
      const result = originalExecute(...args);
      // Re-extract citation strings from updated CKEditor HTML after Full mode save
      setTimeout(() => {
        ctx.isChanged = true;
        if (typeof ctx.onMouseLeave === 'function') {
          ctx.onMouseLeave();
        }
      }, 0);
      return result;
    }

    if (
      !sourceContext?.fromRhp ||
      typeof sourceContext.selectedCitationId !== 'string'
    ) {
      return originalExecute(...args);
    }

    // Step 1: Intercept save in Reference mode — just log, don't close
    console.log(
      '[wk-source-editing] byReference save intercepted',
      sourceContext.selectedCitationId
    );

    // Step 2: Read citation from CodeMirror and update store
    const citationString = getSourceEditingDialogContent(ctx);
    if (typeof citationString === 'string') {
      updateSelectedCitationStringInStore(
        ctx,
        sourceContext.selectedCitationId,
        citationString
      );
      console.log(
        '[wk-source-editing] store updated with citationString',
        citationString.substring(0, 60)
      );
    }

    return undefined;
  };

  ctx.sourceEditingReplaceListenerBound = true;
};

export const navigateSourceEditingReference = (
  ctx: any,
  direction: 'previous' | 'next'
): void => {
  const sourceContext = ctx.sourceEditingContext as SourceEditingContext;

  if (ctx.sourceEditingMode !== 'byReference') {
    return;
  }

  if (!sourceContext?.fromRhp) {
    return;
  }

  const match = findSourceForCitation(ctx, sourceContext.selectedCitationId);
  if (!match) {
    return;
  }

  const { source, index: currentIndex } = match;
  const references = source.list();

  const offset = direction === 'next' ? 1 : -1;
  const nextIndex =
    (currentIndex + offset + references.length) % references.length;

  const nextCitation = references[nextIndex];
  if (!nextCitation?.citationId) {
    return;
  }

  ctx.citationId = nextCitation.citationId;
  ctx.currentSelectedReferenceId = nextCitation.citationId;

  source.onSelect?.(nextCitation.citationId);

  const payload: SourceEditingContext = {
    ownerId: getSourceEditingOwnerId(ctx),
    fromRhp: true,
    timestamp: Date.now(),
    selectedCitationId: nextCitation.citationId,
    citationString: nextCitation.citationString || '',
    referenceIndex: nextIndex,
  };

  ctx.sourceEditingContext = payload;
  ctx.elementRef?.nativeElement?.ownerDocument?.defaultView?.dispatchEvent?.(
    new CustomEvent(SOURCE_CONTEXT_EVENT, { detail: payload })
  );
};

export const handleSourceEditingOpenedFromRhp = (ctx: any): boolean => {
  const selectedContext = resolveSelectedCitationForSourceEditing(ctx);
  if (!selectedContext) {
    return false;
  }

  const { citation, source } = selectedContext;
  const references = source.list();
  const selectedIndex = Math.max(
    findIndex(references, { citationId: citation.citationId }),
    0
  );

  const payload: SourceEditingContext = {
    ownerId: getSourceEditingOwnerId(ctx),
    fromRhp: true,
    timestamp: Date.now(),
    selectedCitationId: citation.citationId,
    citationString: citation.citationString || '',
    referenceIndex: selectedIndex,
  };

  ctx.citationId = citation.citationId;
  ctx.currentSelectedReferenceId = citation.citationId;
  ctx.sourceEditingContext = payload;

  source.onSelect?.(citation.citationId);

  ctx.elementRef?.nativeElement?.ownerDocument?.defaultView?.dispatchEvent?.(
    new CustomEvent(SOURCE_CONTEXT_EVENT, { detail: payload })
  );

  return true;
};

export const getSourceEditingDialogContent = (_ctx: any): string | null => {
  const sourceDialog = document?.querySelector(
    '.ck-dialog-overlay .ck-source-editing-enhanced-dialog'
  );
  const sourceContent =
    sourceDialog?.querySelector?.('.cm-content')?.textContent;

  return typeof sourceContent === 'string' ? sourceContent : null;
};

export const updateSelectedCitationStringInStore = (
  ctx: any,
  selectedCitationId: string,
  citationString: string
): void => {
  const match = findSourceForCitation(ctx, selectedCitationId);
  if (!match) {
    return;
  }

  const { source } = match;
  const updatedReferences = source
    .list()
    .map((reference) =>
      reference.citationId === selectedCitationId
        ? { ...reference, citationString }
        : reference
    );

  source.setList(updatedReferences);
  source.dispatchUpdate(updatedReferences);
};

const resolveSelectedCitationForSourceEditing = (
  ctx: any
): {
  citation: any;
  source: ReferenceSource;
} | null => {
  const selectedContext = getSelectedCitationContextFromRhpClickContext(ctx);
  if (selectedContext) {
    selectedContext.source.onSelect?.(selectedContext.citation.citationId);
    return selectedContext;
  }

  // Fallback: pick the first citation from the first non-empty source
  const sources: ReferenceSource[] = ctx.referenceSources || [];
  for (const source of sources) {
    const list = source.list();
    if (list?.length && list[0]?.citationId) {
      source.onSelect?.(list[0].citationId);
      return { citation: list[0], source };
    }
  }

  return null;
};

const findSourceForCitation = (
  ctx: any,
  citationId: string
): { source: ReferenceSource; index: number } | null => {
  const sources: ReferenceSource[] = ctx.referenceSources || [];
  for (const source of sources) {
    const list = source.list();
    const idx = findIndex(list, { citationId });
    if (idx >= 0) {
      return { source, index: idx };
    }
  }
  return null;
};

const getSelectedCitationContextFromRhpClickContext = (
  ctx: any
): {
  citation: any;
  source: ReferenceSource;
} | null => {
  if (ctx.citationId) {
    const match = findSourceForCitation(ctx, ctx.citationId);
    if (match) {
      return {
        citation: match.source.list()[match.index],
        source: match.source,
      };
    }
  }

  if (ctx.currentSelectedReferenceId) {
    const match = findSourceForCitation(ctx, ctx.currentSelectedReferenceId);
    if (match) {
      return {
        citation: match.source.list()[match.index],
        source: match.source,
      };
    }
  }

  return null;
};

export const refreshSourceEditingContextForTab = (
  ctx: any,
  tabIndex: number
): void => {
  if (ctx.sourceEditingMode !== 'byReference') {
    return;
  }

  const sources: ReferenceSource[] = ctx.referenceSources || [];
  const source = sources[tabIndex];
  if (!source) {
    return;
  }

  const list = source.list();
  if (!list?.length || !list[0]?.citationId) {
    return;
  }

  const citation = list[0];
  source.onSelect?.(citation.citationId);

  ctx.citationId = citation.citationId;
  ctx.currentSelectedReferenceId = citation.citationId;

  const payload: SourceEditingContext = {
    ownerId: getSourceEditingOwnerId(ctx),
    fromRhp: true,
    timestamp: Date.now(),
    selectedCitationId: citation.citationId,
    citationString: citation.citationString || '',
    referenceIndex: 0,
  };

  ctx.sourceEditingContext = payload;
  ctx.elementRef?.nativeElement?.ownerDocument?.defaultView?.dispatchEvent?.(
    new CustomEvent(SOURCE_CONTEXT_EVENT, { detail: payload })
  );
};
